import React, { FC, useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import styled from 'styled-components'
import { addYears } from 'date-fns'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import useText from '../../../common/hooks/useText'
import { ApiCustomer, useLazyApiCustomerGetHashedApiKeyQuery } from '../../../common/redux/api/web-api'
import Select from '../../../common/components/Select'
import { InfoIcon } from '../../../lib/icons/info-icon'
import DatePicker from '../../../common/components/DatePicker'
import Toggle from '../../../common/components/Toggle'
import { dateLocalize } from '../../../lib/date-localize'
import { Button } from '../../../common/components/Button'
import Input from '../../../common/components/Input'
import { CheckboxInput } from '../../../lib/checkbox-input'
import useKeyboardHandle from '../../../common/hooks/useKeyboardHandle'

const STooltip = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 5px;
`

export type CreateApiCustomersModalProps = {
    isOpened?: boolean
    apiCustomer?: ApiCustomer
    onCancel: () => void
    onCreate: (apiCustomer: ApiCustomer) => void
    className?: ClassName
    style?: React.CSSProperties
}

const calculateValidityPeriod = (from?: string, to?: string) => {
    if (!from || !to) return 'custom'

    const dateFrom = new Date(from)
    const nonCustomYearOptions = [1, 2, 3]
    const nonCustomDateDictionary: { [id: string]: number } = {}

    nonCustomYearOptions.forEach((n) => {
        nonCustomDateDictionary[addYears(dateFrom, n).toDateString()] = n * 12
    })

    return nonCustomDateDictionary[new Date(to).toDateString()] ?? 'custom'
}

type ApiCustomerFormData = ApiCustomer & { validityPeriod?: string }

export const CreateApiCustomersModal: FC<CreateApiCustomersModalProps> = ({
    isOpened,
    apiCustomer,
    onCancel,
    onCreate,
    className,
    style,
}) => {
    const text = useText()
    const validityPeriodOptions = [
        {
            key: '12',
            value: text('lblTwelveMonthsValidityInterval'),
        },
        {
            key: '24',
            value: text('lblTwentyFourMonthsValidityInterval'),
        },
        {
            key: '36',
            value: text('lblThirtySixMonthsValidityInterval'),
        },
        {
            key: 'custom',
            value: text('lblCustomValidityInterval'),
        },
    ]
    const validFrom = new Date()
    const [generateApiKey, { data: apiHashKey }] = useLazyApiCustomerGetHashedApiKeyQuery()
    const [isPrivateCustomer, setIsPrivateCustomer] = useState(false)
    const [apiCallbackEnabled, setApiCAllbackEnabled] = useState(false)

    useKeyboardHandle({ onEscapePress: onCancel })

    const schema = yup.object().shape({
        name: yup.string().required(text('msgNameRequired')),
        apiKey: yup.string().required(text('msgApiKeyIsRequired')),
        email: yup
            .string()
            .required(text('msgEmailIsRequired'))
            .typeError(text('msgEmailIsRequired'))
            .email(text('msgEmailInvalid')),
        validTo: yup.string().required(text('msgValidateToDateRequired')),
        callbackBaseUrl: yup
            .string()
            .nullable()
            .when('$apiCallbackEnabled', (apiCallbackEnabled, schema) =>
                !apiCallbackEnabled
                    ? schema
                    : schema
                          .nullable()
                          .typeError(text('msgCallbackBaseUrlRequired'))
                          .required(text('msgCallbackBaseUrlRequired'))
                          .url(text('msgCallbackBaseUrlInvalid'))
            ),
        description: yup.string().typeError(text('msgCompanyNameRequired')).required(text('msgCompanyNameRequired')),
    })
    const {
        register,
        handleSubmit,
        watch,
        reset,
        setValue,
        trigger,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useForm<ApiCustomerFormData>({
        resolver: yupResolver(schema, { abortEarly: false }),
        values: {
            ...apiCustomer,
            validFrom: apiCustomer
                ? new Date(dateLocalize(apiCustomer?.validFrom)).toLocaleDateString('sv-SE')
                : validFrom.toLocaleDateString('sv-SE'),
            validTo: new Date(dateLocalize(apiCustomer?.validTo)).toLocaleDateString('sv-SE'),
            validityPeriod: apiCustomer ? calculateValidityPeriod(apiCustomer.validFrom, apiCustomer.validTo) : '36',
            callbackEnabled: apiCustomer?.callbackEnabled ?? false,
        },
        mode: 'all',
        reValidateMode: 'onChange',
        context: { apiCallbackEnabled },
    })

    useEffect(() => {
        if (apiCustomer) {
            trigger()
            setApiCAllbackEnabled(apiCustomer?.callbackEnabled ?? false)
            setIsPrivateCustomer(apiCustomer.description === text('msgPrivateDescription'))
        }
    }, [apiCustomer])

    useEffect(() => {
        if (apiHashKey) {
            setValue('apiKey', apiHashKey?.key, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
        }
    }, [apiHashKey])

    useEffect(() => {
        trigger('callbackBaseUrl')
    }, [trigger, apiCallbackEnabled])

    const watchValidityPeriod = watch('validityPeriod')

    const onSubmit = (data: ApiCustomerFormData) => {
        let validTo = new Date()
        if (data.validTo && data.validityPeriod === 'custom') {
            validTo = new Date(data.validTo)
        } else {
            validTo.setMonth(validFrom.getMonth() + parseInt(data.validityPeriod ?? '0', 10))
        }

        onCreate({
            ...data,
            description: isPrivateCustomer ? text('msgPrivateDescription') : data.description,
            validFrom: validFrom.toDateString(),
            validTo: validTo.toDateString(),
            callbackEnabled: apiCallbackEnabled,
            enabled: true,
        })
    }

    const handleGenerateApiKey = async () => {
        await generateApiKey()
    }

    return isOpened ? (
        <BootstrapModalLayout
            data-testid="layout-container"
            title={
                apiCustomer ? text('lblEAPICustomerDetails', { name: apiCustomer.name }) : text('lblNewEAPICustomer')
            }
            className={cn(className)}
            style={style}
            size={'lg'}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    <div className="form-group col-sm-6">
                        <CheckboxInput
                            checked={isPrivateCustomer}
                            setChecked={(checked) =>
                                setIsPrivateCustomer(() => {
                                    setValue('description', checked ? text('msgPrivateDescription') : null, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                    return checked
                                })
                            }
                            dataTestId={'modal-form-checkbox'}
                            label={text('msgPrivateCustomer')}
                        />
                    </div>
                </div>
                {!isPrivateCustomer ? (
                    <div className="row margin-bottom-xsmall">
                        <div className="form-group col-sm-6">
                            <STooltip>
                                <InfoIcon title={text('msgCompanyNameIsRequiredTooltip')} size={20} />
                            </STooltip>
                            <Input
                                {...register('description')}
                                labelText={text('lblOrganisationDescription')}
                                id="modal-form-description"
                                data-testid="modal-form-description"
                                type="text"
                                error={errors.description?.message}
                                required
                            />
                        </div>
                    </div>
                ) : null}
                <div className="modal-form margin-top-xsmall" data-testid="modal-form">
                    <div className="form-group">
                        <STooltip>
                            <InfoIcon title={text('msgNameIsRequiredTooltip')} size={20} />
                        </STooltip>
                        <Input
                            {...register('name')}
                            labelText={text('lblNameField')}
                            id="modal-form-name"
                            data-testid="modal-form-name"
                            type="text"
                            error={errors.name?.message}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <STooltip>
                            <InfoIcon title={text('msgEmailIsRequiredTooltip')} size={20} />
                        </STooltip>
                        <Input
                            {...register('email')}
                            id="modal-form-email"
                            labelText={text('lblEmailField')}
                            type="text"
                            data-testid="modal-form-email"
                            error={errors.email?.message}
                            required
                        />
                    </div>
                </div>

                <div className="form-group margin-top-xsmall" data-testid="modal-form-api-key">
                    <div className="row">
                        <div className="col-sm-6 margin-top-25">
                            <Input
                                {...register('apiKey')}
                                id="modal-input-apikey"
                                data-testid="modal-input-apikey"
                                labelText={text('lblApiKey')}
                                error={errors.apiKey?.message}
                                required
                                disabled
                            />
                            <Button
                                type="button"
                                data-testid="modal-button-generate"
                                onClick={handleGenerateApiKey}
                                primary
                                className="margin-top-xsmall"
                                label={text('btnGenerate')}
                            />
                        </div>
                        <div className="col-sm-6">
                            <STooltip>
                                <InfoIcon title={text('validityPeriodIsRequiredTooltip')} size={20} />
                            </STooltip>
                            <Select
                                {...register('validityPeriod')}
                                data-testid="validity-period-select"
                                options={validityPeriodOptions}
                            />
                        </div>
                    </div>
                </div>

                {watchValidityPeriod && watchValidityPeriod === 'custom' ? (
                    <div className="modal-form margin-top-med" data-testid="modal-form">
                        <div className="form-group">
                            <DatePicker
                                {...register('validFrom')}
                                data-testid="valid-from-input"
                                id="creationDate"
                                disabled
                                defaultValue={validFrom.toLocaleDateString('sv-SE')}
                                labelText={text('lblFromField')}
                            />
                        </div>

                        <div className="form-group">
                            <DatePicker
                                {...register('validTo')}
                                data-testid="valid-to-input"
                                id="expireDate"
                                min={validFrom.toLocaleDateString('se-SE')}
                                error={errors.validTo?.message}
                                labelText={text('lblToField')}
                            />
                        </div>
                    </div>
                ) : null}

                <div className="form-group margin-top-xsmall checkbox-form">
                    <label>
                        <dt className="margin-right-small">{text('lblApiCallback')}</dt>
                    </label>
                    <Toggle
                        data-testid="api-callback-toggle"
                        isToggled={apiCallbackEnabled}
                        toggleFn={() => {
                            setApiCAllbackEnabled((value) => {
                                setValue('callbackEnabled', !value, { shouldValidate: true, shouldDirty: true })
                                return !value
                            })
                        }}
                    />
                </div>

                <div className="form-group margin-top-xsmall margin-bottom-med">
                    <Input
                        {...register('callbackBaseUrl')}
                        data-testid="modal-input-callback-base-url"
                        id="modal-input-callback-base-url"
                        type="text"
                        labelText={text('lblCallbackBaseUrl')}
                        required
                        disabled={!apiCallbackEnabled}
                        error={errors.callbackBaseUrl?.message}
                    />
                </div>

                <div className="modal-footer margin-top-med" data-testid="modal-footer-buttons">
                    <Button
                        primary={false}
                        label={text('btnCancel')}
                        type="button"
                        data-testid="modal-cancel-button"
                        onClick={() => {
                            reset()
                            onCancel()
                        }}
                    />
                    <Button
                        primary
                        label={text(apiCustomer ? 'btnSave' : 'btnCreate')}
                        disabled={!isDirty || isSubmitting || !isValid}
                        type="submit"
                        data-testid="modal-submit-button"
                    />
                </div>
            </form>
        </BootstrapModalLayout>
    ) : null
}
